import { useMemo, useState } from 'react';
import { Alert, App, Button, Select } from 'antd';
import { LayoutGrid } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsStringLiteral, useQueryStates } from 'nuqs';
import {
  DASHBOARD_ACCESS_ROLES,
  DASHBOARD_SHORTCUT_CAPABILITY,
  DASHBOARD_SHORTCUT_TARGETS,
  ORG_UNIT_TYPES,
  can,
  defaultLayoutEntriesFor,
  readableDate,
  saveDashboardLayoutSchema,
} from '@pams/shared';
import type { DashboardLayoutConfigDto, DashboardTileId } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { ACCESS_ROLE_LABEL, DASHBOARD_TILE_LABEL, ORG_UNIT_TYPE_LABEL, accessRoleLabel } from '@/lib/labels';
import { PageHeader } from '@/components/PageHeader';
import { FilterField } from '@/components/FilterField';
import { ReorderableList } from '@/components/ReorderableList';
import { TwoColumnLayout } from '@/components/TwoColumnLayout';
import { Skeleton } from '@/components/Skeleton';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { LAYOUT_COPY } from './copy';
import { TileEntryRow } from './components/TileEntryRow';
import { TileLibrary } from './components/TileLibrary';
import { LayoutPreview } from './components/LayoutPreview';
import {
  addEntry,
  isDirty,
  loadDraft,
  patchEntry,
  removeEntry,
  reorderEntries,
  toSavePayload,
  type LayoutDraft,
} from './draft';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE DASHBOARD CONFIGURATOR. [P6b]
 *
 *  A super admin composes what each `(accessRole, orgUnitType)` combination opens on. The
 *  TILE REGISTRY IS CLOSED here — this screen arranges layouts, it does not define tiles.
 *
 *  ⚠️ THERE IS NO READ-ONLY MODE, AND THAT IS DELIBERATE. `/policies` and `/post-types` render
 *  read-only for a role without the write key; this route is gated on `dashboard.configure`,
 *  which HAS no read half, so anybody who reaches it can save. `/policies/new`'s convention:
 *  a screen with no read-only meaning gets the write capability and a `/forbidden` bounce.
 *  Do not add a `canWrite` branch — it would be dead, and it would cost a `Tooltip` that
 *  `census.spec.ts` then has to account for.
 *
 *  ⚠️ AND `useUnsavedGuard` CANNOT SEE THE COMBINATION PICKER. Its three listeners are
 *  `beforeunload`, a capture-phase anchor click and `popstate`; a nuqs `setState` is none of
 *  them. So the picker calls the SAME `confirm` the guard uses, before switching — one dirty
 *  check, one sentence, two callers, never two guards that disagree.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/* ⚠️ [C4a §8] The role picker names a CATEGORY chosen beside a separate level picker — generic words. */
const ROLE_OPTIONS = DASHBOARD_ACCESS_ROLES.map((role) => ({ value: role, label: ACCESS_ROLE_LABEL[role] }));
const LEVEL_OPTIONS = ORG_UNIT_TYPES.map((type) => ({ value: type, label: ORG_UNIT_TYPE_LABEL[type] }));

const DashboardLayoutsPage = () => {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [combo, setCombo] = useQueryStates(
    {
      /* `parseAsStringLiteral` is the client half of the server's `z.enum` — `useServerTable`'s rule. */
      role: parseAsStringLiteral(DASHBOARD_ACCESS_ROLES).withDefault('SUPER_ADMIN'),
      level: parseAsStringLiteral(ORG_UNIT_TYPES).withDefault('COMMISSIONERATE'),
    },
    { history: 'replace', clearOnDefault: true },
  );

  /**
   * ⚠️ ONLY THE OPERATOR'S EDITS ARE STATE. What the server said is DERIVED from the query,
   * and the draft on screen is "their edits if they belong to this combination, otherwise the
   * server's".
   *
   * The obvious shape — a `loaded` state seeded by an effect — is refused by
   * `react-hooks/set-state-in-effect`, and CLAUDE.md says why that rule is right: a `setState`
   * in an effect body is a cascading render, and seeding is a consequence of data arriving
   * rather than a synchronisation with an external system. It is also a data-loss bug waiting
   * to happen: an operator switches combination while a refetch for the previous one is in
   * flight, the OLD answer lands, and the effect seeds the NEW combination from it ([P3b.3]'s
   * class). Keying the edits on the combination makes that unrepresentable — a stale answer
   * cannot match a key it was never for.
   */
  const [edited, setEdited] = useState<LayoutDraft | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const config = useQuery({
    queryKey: queryKeys.dashboardLayouts.config(combo.role, combo.level),
    queryFn: () => api.get<DashboardLayoutConfigDto>(`/dashboard-layouts/${combo.role}/${combo.level}`),
  });

  const payload = config.data;
  const loaded = useMemo(() => (payload ? loadDraft(payload) : null), [payload]);

  /*
   * ⚠️ EDITS BELONG TO THE COMBINATION THEY WERE MADE AGAINST, AND THAT IS THE ANSWER'S KEY
   * RATHER THAN THE URL'S.
   *
   * `loadDraft` keys on `config.target` — what the SERVER said this payload is for — and
   * comparing that against the URL looked equivalent and is not: the two differ for exactly as
   * long as a request is in flight, so an edit made in that window was silently DISCARDED, and
   * the screen simply reverted with nothing to say why. Keying on the loaded row instead makes
   * a stale answer landing drop the edits made against the one it replaced, which is the
   * behaviour [P3b.3] cost a release to learn.
   */
  const draft = loaded && edited?.key === loaded.key ? edited : loaded;
  const dirty = isDirty(draft, loaded);

  /** Every mutation goes through here, so nothing can edit a combination it is not for. */
  const editDraft = (change: (current: LayoutDraft) => LayoutDraft): void => {
    if (!loaded) return;
    setEdited((current) => change(current?.key === loaded.key ? current : loaded));
  };

  useUnsavedGuard({
    when: dirty,
    confirm: () =>
      new Promise<boolean>((resolve) => {
        modal.confirm({
          title: LAYOUT_COPY.unsaved,
          okText: 'Leave',
          onOk: () => {
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      }),
  });

  const save = useMutation<DashboardLayoutConfigDto, ApiError, LayoutDraft>({
    mutationFn: (next) =>
      api.put<DashboardLayoutConfigDto>(`/dashboard-layouts/${combo.role}/${combo.level}`, toSavePayload(next)),
    onSuccess: (result) => {
      /* ⚠️ THE EDITS ARE DROPPED, NOT RE-SEEDED. The server's answer becomes the draft through
         the derivation above, so there is exactly one place a draft can come from. */
      setEdited(null);
      setErrors([]);
      /*
       * ⚠️ `setQueryData` FOR OUR OWN ROW AND AN INVALIDATION FOR THE DASHBOARD.
       * The first neither refetches nor marks stale, so there is no scheduled refetch for the
       * seed effect above to race ([P4]'s rule). The second IS the right breadth: an override
       * changes every `view(params)` and every `rows(...)` at once, and nobody is editing a
       * record under that prefix. Unconditional even when the saved combination is not the
       * operator's own — a "does this affect me" predicate would be a second copy of the
       * resolver's row-picking rule INCLUDING the role-borrow rung, and getting it wrong
       * yields a stale dashboard nobody can explain.
       */
      queryClient.setQueryData(queryKeys.dashboardLayouts.config(combo.role, combo.level), result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      message.success(LAYOUT_COPY.saved);
    },
    onError: (error) => {
      setErrors([error.message]);
    },
  });

  const reset = useMutation<DashboardLayoutConfigDto, ApiError, void>({
    mutationFn: () => api.post<DashboardLayoutConfigDto>(`/dashboard-layouts/${combo.role}/${combo.level}/reset`),
    onSuccess: (result) => {
      setEdited(null);
      queryClient.setQueryData(queryKeys.dashboardLayouts.config(combo.role, combo.level), result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: (error) => {
      setErrors([error.message]);
    },
  });

  const switchCombination = (
    next: Partial<{ role: (typeof DASHBOARD_ACCESS_ROLES)[number]; level: (typeof ORG_UNIT_TYPES)[number] }>,
  ) => {
    const go = () => void setCombo(next);
    if (!dirty) {
      go();
      return;
    }
    modal.confirm({ title: LAYOUT_COPY.unsaved, okText: 'Discard', onOk: go });
  };

  const confirmReset = () => {
    const titles = defaultLayoutEntriesFor(combo.role, combo.level).map(
      (entry) => DASHBOARD_TILE_LABEL[entry.tileId as DashboardTileId].title,
    );
    modal.confirm({
      title: LAYOUT_COPY.resetTitle,
      /* ⚠️ THE OUTCOME, NOT THE VERB — the operator reads what they will get back. */
      content: (
        <div className="flex flex-col gap-2 text-sm">
          <p>{LAYOUT_COPY.resetBody}</p>
          <ol className="list-decimal pl-5">
            {titles.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ol>
          <p className="text-muted-foreground">{LAYOUT_COPY.resetOverrides}</p>
        </div>
      ),
      okText: LAYOUT_COPY.resetConfirm,
      okButtonProps: { danger: true },
      onOk: () => {
        reset.mutate();
      },
    });
  };

  /*
   * ⚠️ [P6b.2] THE LIST'S ITEM ID IS THE PLACEMENT'S, NEVER THE TILE'S.
   *
   * `ReorderableList` hands `onReorder` an array of item ids and `reorderEntries` looks each
   * one up — so with a tile-keyed id two shortcuts are ONE key, the lookup drops one of them,
   * and a `Move to top` on a three-tile layout silently returns two. It is the identity rule
   * §7 already states for a slot key and a duty point id, one layer up: derive from the
   * identity, never from the position or from what the thing happens to BE.
   *
   * ⚠️ THE LABEL IS STILL THE TILE'S TITLE, and two placements of one tile therefore share
   * an accessible name ("Move Shortcut up"). That is the honest reading — an operator moving
   * the second door is moving a shortcut — and the position number the row renders beside it
   * is what tells them apart.
   */
  const items = useMemo(
    () =>
      (draft?.entries ?? []).map((entry) => ({
        id: entry.instanceId,
        label: DASHBOARD_TILE_LABEL[entry.tileId].title,
      })),
    [draft?.entries],
  );
  const onLayoutIds = useMemo(() => new Set((draft?.entries ?? []).map((entry) => entry.tileId)), [draft?.entries]);
  /*
   * ⚠️ [C4a §8] A NODAL ROLE AT A LEVEL IS NAMED FOR THE LEVEL — `Division Admin`, not
   * `Nodal admin · Division`. The level is appended only where the label did not already say it
   * (`Super admin · Commissionerate`), so the combination is never named twice.
   */
  const levelledLabel = accessRoleLabel(combo.role, combo.level);
  const label =
    levelledLabel === ACCESS_ROLE_LABEL[combo.role]
      ? `${levelledLabel} · ${ORG_UNIT_TYPE_LABEL[combo.level]}`
      : levelledLabel;
  /*
   * ⚠️ [P6b.1] THE DESTINATIONS THE **TARGET** ROLE COULD OPEN, NOT THE ONES THIS SUPER ADMIN
   * CAN. A super admin can open everything, so filtering on their own access would offer a
   * NODAL_MONITOR layout a shortcut to `/arrangements/new` — which the endpoint then refuses,
   * turning a control into a 422. Same predicate the server uses, same argument as §2a.
   */
  const reachableTargets = useMemo(
    () => DASHBOARD_SHORTCUT_TARGETS.filter((target) => can(combo.role, DASHBOARD_SHORTCUT_CAPABILITY[target])),
    [combo.role],
  );
  const empty = draft !== null && draft.entries.length === 0;

  const provenance = !payload
    ? null
    : payload.configured && payload.updatedByName && payload.updatedAt
      ? LAYOUT_COPY.arrangedBy(payload.updatedByName, readableDate(payload.updatedAt) ?? payload.updatedAt)
      : payload.layoutSource === 'ROLE'
        ? LAYOUT_COPY.borrowed
        : LAYOUT_COPY.neverEdited;

  const left = (
    <div className="flex flex-col gap-4">
      {errors.length > 0 && <Alert type="error" showIcon message={errors.join(' ')} />}
      {/* §4a — surfaced before the server refuses it, and the floor's names are BUILT. */}
      {empty && <Alert type="warning" showIcon message={LAYOUT_COPY.emptyRefusal()} data-testid="empty-refusal" />}

      <div>
        <h2 className="text-sm font-medium text-foreground">{LAYOUT_COPY.onLayout}</h2>
        <p className="text-xs text-muted-foreground">{LAYOUT_COPY.onLayoutHint}</p>
        <p className="text-xs text-muted-foreground">{LAYOUT_COPY.widthNote}</p>
      </div>

      {config.isPending || !draft ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ReorderableList
          items={items}
          ariaLabel={LAYOUT_COPY.onLayout}
          data-testid="layout-order"
          layout="block"
          emptyText={LAYOUT_COPY.libraryEmpty}
          onReorder={(next) => {
            editDraft((current) =>
              reorderEntries(
                current,
                next.map((item) => item.id),
              ),
            );
          }}
          renderItem={(_item, index, controls) => {
            const entry = draft.entries[index];
            if (!entry) return null;
            return (
              <TileEntryRow
                entry={entry}
                index={index}
                controls={controls}
                reachableTargets={reachableTargets}
                onChange={(patch) => {
                  editDraft((current) => patchEntry(current, entry.instanceId, patch));
                }}
                onRemove={() => {
                  editDraft((current) => removeEntry(current, entry.instanceId));
                }}
              />
            );
          }}
        />
      )}
    </div>
  );

  const right = payload ? (
    <aside aria-label={LAYOUT_COPY.library}>
      <TileLibrary
        config={payload}
        onLayoutIds={onLayoutIds}
        onAdd={(id: DashboardTileId) => {
          editDraft((current) => addEntry(current, id, reachableTargets));
        }}
      />
    </aside>
  ) : (
    <Skeleton className="h-64 w-full" />
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={LayoutGrid}
        title={LAYOUT_COPY.title}
        subtitle={
          <>
            {LAYOUT_COPY.subtitle}
            {provenance ? <span className="block text-xs text-muted-foreground">{provenance}</span> : null}
          </>
        }
        filters={
          <div className="flex flex-wrap gap-3">
            <FilterField label={LAYOUT_COPY.role}>
              <Select
                className="min-w-48"
                options={ROLE_OPTIONS}
                value={combo.role}
                onChange={(role) => {
                  switchCombination({ role });
                }}
              />
            </FilterField>
            <FilterField label={LAYOUT_COPY.level}>
              <Select
                className="min-w-48"
                options={LEVEL_OPTIONS}
                value={combo.level}
                onChange={(level) => {
                  switchCombination({ level });
                }}
              />
            </FilterField>
          </div>
        }
        actions={
          <>
            <Button onClick={confirmReset} loading={reset.isPending}>
              {LAYOUT_COPY.reset}
            </Button>
            {/*
              ⚠️ NO TOOLTIP ON THE DISABLED SAVE, DELIBERATELY. §8's own rule is that a
              disabled control with its reason behind a hover is the failure mode this repo
              keeps re-finding — hover is unavailable on touch and unreachable from a
              keyboard. The reason is a PERSISTENT `Alert` above the list instead, readable at
              rest. That also keeps this page out of `census.spec.ts`'s ledger entirely: a
              tooltip whose `title` is `undefined` mounts no node, so it could only ever have
              been an EXEMPT entry — an accounting line for an affordance that should not
              exist.
            */}
            <Button
              type="primary"
              disabled={empty || !dirty}
              loading={save.isPending}
              onClick={() => {
                if (!draft) return;
                /* ⚠️ THE SAME SHARED SCHEMA THE SERVER PARSES — no hand-typed rules here. */
                const parsed = saveDashboardLayoutSchema.safeParse(toSavePayload(draft));
                if (!parsed.success) {
                  setErrors(parsed.error.issues.map((issue) => issue.message));
                  return;
                }
                save.mutate(draft);
              }}
            >
              {LAYOUT_COPY.save}
            </Button>
          </>
        }
      />

      <TwoColumnLayout variant="form" left={left} right={right} secondaryLabel={LAYOUT_COPY.library} />

      {draft && <LayoutPreview draft={draft} label={label} />}
    </div>
  );
};

export default DashboardLayoutsPage;
